import { useCallback, useEffect, useState } from "react";

import jobCommandOneliner from "~/lib/jobCommandOneliner";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
  HoverCardPortal,
} from "~/components/shared/HoverCard";

function useIsTruncated(element: HTMLElement | null) {
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    if (!element) {
      return;
    }

    function updateIsTruncated() {
      if (!element) {
        return;
      }
      setIsTruncated(element.scrollWidth > element.clientWidth);
    }

    updateIsTruncated();

    const observer = new ResizeObserver(updateIsTruncated);
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [element]);

  return isTruncated;
}

export default function Command({
  command,
  onNestedLinkTrigger,
}: {
  command: string;
  onNestedLinkTrigger: (e: React.MouseEvent | React.KeyboardEvent) => void;
}) {
  const [commandElement, setCommandElement] = useState<HTMLSpanElement | null>(null);
  const commandRef = useCallback((node: HTMLSpanElement | null) => setCommandElement(node), []);
  const isTruncated = useIsTruncated(commandElement);

  return (
    <HoverCard>
      <HoverCardTrigger asChild={true}>
        <span className="small job-command flex min-w-0 flex-auto font-mono">
          <span className="pointer-events-none max-w-full truncate" ref={commandRef}>
            {jobCommandOneliner(command)}
          </span>
        </span>
      </HoverCardTrigger>

      {isTruncated && (
        <HoverCardPortal>
          <HoverCardContent
            className="z-50"
            hideArrow={true}
            size="max-lg"
            onClick={onNestedLinkTrigger}
            onKeyDown={onNestedLinkTrigger}
            data-testid="job-command-hover-card"
          >
            <pre className="monospace whitespace-pre-wrap break-words text-xs">{command}</pre>
          </HoverCardContent>
        </HoverCardPortal>
      )}
    </HoverCard>
  );
}
